import { useEffect, useState } from "react";
import { getSupabaseClient, useAuthStore } from "@veorest/api-client";
import { cacheTenant, getCachedTenant } from "@veorest/pwa";

interface TenantData {
  id: string;
  name: string;
  currency: string;
}

export function useTenant() {
  const { user } = useAuthStore();
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const supabase = getSupabaseClient();

    (async () => {
      // Try IndexedDB cache first (instant offline recovery)
      try {
        const cached = await getCachedTenant(user.id);
        if (cached && !cancelled) {
          setTenant({ id: cached.id, name: cached.name, currency: cached.currency });
          setLoading(false);
        }
      } catch { /* cache read failed — continue to network */ }

      // Fetch from Supabase (updates cache)
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("tenant_id, display_name")
          .eq("user_id", user.id)
          .maybeSingle();

        if (cancelled) return;

        if (!profile?.tenant_id) {
          if (!tenant) setLoading(false);
          return;
        }

        const { data: t } = await supabase
          .from("tenants")
          .select("id, name, currency")
          .eq("id", profile.tenant_id)
          .maybeSingle();

        if (cancelled) return;

        if (t) {
          const td: TenantData = { id: t.id as string, name: t.name as string, currency: t.currency as string };
          setTenant(td);
          // Cache for offline use
          cacheTenant({
            id: td.id,
            name: td.name,
            currency: td.currency,
            profile_tenant_id: profile.tenant_id as string,
            user_id: user.id,
            cached_at: Date.now(),
          });
        }
      } catch {
        // Network failed — already loaded from cache above, so tenant is set
      }

      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user]);

  return { tenant, loading };
}
